import { useContext, useEffect } from 'react';
import { AuthContext } from "@/features/auth/Context/AuthContext";
import { useLocation } from "wouter";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion } from 'framer-motion';
import { Loader2, Warehouse, BarChart3, FileCheck } from "lucide-react";
import { loginSchema } from "@/features/auth/auth.schemas";
import logoIRB from "@/assets/images/irb.svg";
import { containerVariants, formVariants, sidebarVariants } from '@/components/animations/animations-motion';



export default function SignIn() {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("AuthContext not found. Did you wrap your app with <AuthProvider />?");
  const { user, login, isLoading } = auth;
  const [, navigate] = useLocation();

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberme: false
    },
  });

  useEffect(() => {
    if (user) {
      if(!user.needs_password_reset){
        navigate("/");
      } else {
        navigate("/password-reset-page");
      }
      
    }
  }, [user, navigate]);

  const onLoginSubmit = async (values: z.infer<typeof loginSchema>) => {
    const { email, password, rememberme } = values;
    await login({ email, password, rememberme });
  };

return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen flex items-center justify-center bg-[var(--color-background)] px-4 py-6"
      >
      <div className="max-w-6xl w-full flex flex-col lg:flex-row bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        {/* Panel informativo - Empresas */}
        <motion.div 
          variants={sidebarVariants}
          className="hidden lg:flex lg:w-1/2 bg-white text-gray-800 p-12 flex-col justify-between relative overflow-hidden"
        >
          {/* Logo en área blanca */}
          <div className="relative z-10">
            <div className="flex justify-center items-center mb-8">
              <img src={logoIRB} alt="IRB Gestión" />
            </div>
            
            <div className="bg-white text-[var(--color-primary)] hidden md:block p-8 rounded-2xl">
              <h2 className="text-3xl font-bold mb-4 leading-tight text-center">
                Sistema Integral de Gestión
              </h2>
              
              <p className="text-center mb-6 text-[var(--color-secondary)] leading-relaxed">
                Plataforma para la gestión eficiente de almacenes, 
                órdenes de trabajo y certificaciones.
              </p>
            </div>
          </div>

          {/* Características */}
          <div className="relative z-10">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[var(--color-success)] rounded-full flex items-center justify-center flex-shrink-0">
                  <Warehouse className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm">Control de Inventario</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[var(--color-accent)] rounded-full flex items-center justify-center flex-shrink-0">
                  <FileCheck className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm">Certificaciones</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[var(--color-secondary)] rounded-full flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm">Órdenes de Trabajo</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[var(--color-primary)] rounded-full flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm">Reportes y KPIs</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Panel de Login - Con gradiente */}
        <motion.div 
          variants={formVariants}
          className="w-full lg:w-1/2 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] p-8 sm:p-12"
        >
          {/* Header móvil con fondo blanco */}
          <div className="lg:hidden mb-8 text-center bg-white rounded-2xl p-6 shadow-lg max-w-md mx-auto">
            <div className="flex items-center justify-center mb-4">
              <img src={logoIRB} alt="IRB Gestión" className="h-12" />
            </div>
            <h2 className="text-2xl font-bold leading-tight text-center">
              Sistema Integral de Gestión
            </h2>
          </div>

          <div className="max-w-md mx-auto">
            {/* Formulario con fondo blanco */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Iniciar Sesión
                </h2>
                <p className="text-gray-600">
                  Accede al sistema de gestión
                </p>
              </div>

              <Form {...loginForm}>
                <motion.form 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  onSubmit={loginForm.handleSubmit(onLoginSubmit)} 
                  className="space-y-6"
                  >
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700 font-medium">
                          Correo Electrónico
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="correo@empresa.com" 
                            autoComplete="username"
                            className="h-12 text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)]" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage className='text-red-600'/>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700 font-medium">
                          Contraseña
                        </FormLabel>
                        <FormControl>
                          <Input 
                            type="password" 
                            placeholder="Ingresa tu contraseña" 
                            autoComplete="current-password"
                            className="h-12 text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)]" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage className='text-red-600'/>
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FormField
                        control={loginForm.control}
                        name="rememberme"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2">
                            <FormControl>
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={field.onChange}
                                className="w-4 h-4 text-[var(--color-accent)] border-gray-300 rounded focus:ring-[var(--color-accent)]"
                              />
                            </FormControl>
                            <FormLabel className="text-sm text-gray-600 cursor-pointer">
                              Recordar sesión
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <a 
                      href="mailto:soporte@codilant.com" 
                      className="text-sm font-medium text-[var(--color-accent)] hover:text-[var(--color-secondary)] transition-colors"
                    >
                      ¿Olvidó su contraseña?
                    </a>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] hover:scale-105 hover:text-white text-white font-semibold text-base transition-all duration-200 shadow-lg hover:shadow-xl"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Iniciando sesión...
                      </>
                    ) : (
                      "Iniciar Sesión"
                    )}
                  </Button>
                </motion.form>
              </Form>
            </div>

            {/* Footer */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="mt-6 text-center"
              >
              <p className="text-sm text-white">
                ¿Problemas para acceder?{" "}
                <a 
                  href="mailto:soporte@codilant.com" 
                  className="font-medium text-white hover:text-gray-200 transition-colors underline"
                >
                  Contactar al soporte
                </a>
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );;
}